// ==================================================
// IMPORTS
// ==================================================
import React, { useState, useEffect, useContext, useRef } from 'react';
import { toast } from 'react-toastify';
import { StatusBannerContext } from '../../components/shared/StatusBannerContext';
import '../../components/shared/StatusBanner.css';
import './ComposeMessage.css';
import {
  Send,
  ClipboardCopy,
  Download,
  Upload,
  Paperclip,
  SmilePlus,
  Bold,
  Italic,
  Underline,
  MoreHorizontal,
  Trash2,
  Minus,
  Maximize2,
  Minimize2,
  X,
  UserRoundPlus,
  Square,
  BookOpen,
  MailCheck,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  IndentDecrease,
  IndentIncrease,
  Quote,
  Strikethrough,
  Eraser,
  ChevronDown,
} from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { loadGlobalTags } from '../../utils/tagutils';
import FolderDropdown from './FolderDropdown';
import { createPortal } from 'react-dom';

// ==================================================
// COMPONENT SETUP
// Purpose: Receives props and initializes refs/context.
// ==================================================

const ComposeMessage = ({
  onSend,
  folders = [],
  initialTo = '',
  initialSubject = '',
  initialBody = '',
  initialTags = [],
  onClose,
}) => {

  const importDraftInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const bodyTextareaRef = useRef(null);
  const scheduleMenuRef = useRef(null);
  const formatMenuRef = useRef(null);
  const { showBanner } = useContext(StatusBannerContext);
  const moreToolsRef = useRef(null);
  const textColorMenuRef = useRef(null);
  const alignMenuRef = useRef(null);
  const moreFormattingMenuRef = useRef(null);
  const lastEditorHtmlRef = useRef('');

  // ==================================================
  // FORM STATE
  // Purpose: Stores message fields, folder, tags, attachments, and scheduling.
  // ==================================================
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState(initialTags || []);
  const [folder, setFolder] = useState('Inbox');
  const [isSending, setIsSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showCCBcc, setShowCCBcc] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [enableScheduling, setEnableScheduling] = useState(false);
  const [showScheduleMenu, setShowScheduleMenu] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('05:30 AM');
  const [showCC, setShowCC] = useState(false);
  const [showBCC, setShowBCC] = useState(false);
  const [composeMode, setComposeMode] = useState('page');
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const [selectedFont, setSelectedFont] = useState('Sans Serif');
  const [selectedSize, setSelectedSize] = useState('Small');
  const [showMoreToolsMenu, setShowMoreToolsMenu] = useState(false);
  const [showMailMergeMenu, setShowMailMergeMenu] = useState(false);
  const [mailMergeEnabled, setMailMergeEnabled] = useState(false);
  const [showMailMergeHelp, setShowMailMergeHelp] = useState(false);
  const [showSmartAudienceBuilder, setShowSmartAudienceBuilder] = useState(false);
  const [audienceRecords, setAudienceRecords] = useState([]);
  const [isLoadingAudience, setIsLoadingAudience] = useState(false);
  const [formData, setFormData] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    attachments: [],
    scheduleDate: '',
    scheduleTime: '',
  });

  const [showTextColorMenu, setShowTextColorMenu] = useState(false);
  const [showAlignMenu, setShowAlignMenu] = useState(false);
  const [showMoreFormattingMenu, setShowMoreFormattingMenu] = useState(false);

  // ==================================================
  // SMART AUDIENCE BUILDER
  // Purpose:
  // Controls recipient selection panels.
  // ==================================================

  const [selectedAudienceSource, setSelectedAudienceSource] = useState(null);

  const [audienceSearch, setAudienceSearch] = useState('');

  const [selectedRecipients, setSelectedRecipients] = useState([]);


  const smartAudienceSources = [
    {
      group: 'Families',
      sources: [
        'Parent Contacts',
        'Classroom Rosters',
        'Authorized Pickup Contacts',
        'Emergency Contacts',
      ],
    },
    {
      group: 'Students',
      sources: [
        'Attendance Groups',
        'Missing Documents',
        'Birthday Lists',
        'Medical Alerts',
        'Allergy Groups',
      ],
    },
    {
      group: 'Staff',
      sources: [
        'Staff Groups',
        'Teachers',
        'Directors',
        'Administrators',
        'Floaters/Substitutes',
        'Substitutes',
        'Kitchen',
        'Transportation Staff',
        'Health',
        'Office',
        'Maintenance',
      ],
    },
    {
      group: 'Financial',
      sources: [
        'Billing Lists',
        'Past Due Accounts',
        'Payment Plans',
      ],
    },
    {
      group: 'Operations',
      sources: [
        'Incident Reports',
        'Field Trip Groups',
        'Transportation Lists',
        'Custom Saved Audiences',
        'Spreadsheet Import',
      ],
    },
  ];

  const getAudienceCount = (source) => {
    const counts = {
      'Parent Contacts': '2,347 contacts',
      'Classroom Rosters': '18 classrooms',
      'Authorized Pickup Contacts': '842 contacts',
      'Emergency Contacts': '719 contacts',
      'Attendance Groups': 'Live groups',
      'Missing Documents': '64 students',
      'Birthday Lists': 'This month',
      'Medical Alerts': '31 alerts',
      'Allergy Groups': '27 students',
      'Staff Groups': '86 staff',
      Teachers: '42 teachers',
      Directors: '8 directors',
      Administrators: '12 admins',
      'Floaters/Substitutes': '24 staff',
      'Billing Lists': '326 families',
      'Past Due Accounts': '74 families',
      'Payment Plans': '19 families',
      'Incident Reports': 'Recent reports',
      'Field Trip Groups': 'Active trips',
      'Transportation Lists': 'Bus/van routes',
      'Custom Saved Audiences': 'Saved lists',
      'Spreadsheet Import': 'CSV / Excel',
    };

    return counts[source] || 'Available';
  };

  const getAudienceIcon = (source) => {
    if (source.includes('Parent') || source.includes('Pickup') || source.includes('Emergency')) return '👨‍👩‍👧';
    if (source.includes('Classroom')) return '🏫';
    if (source.includes('Attendance')) return '📋';
    if (source.includes('Billing') || source.includes('Past Due') || source.includes('Payment')) return '💰';
    if (source.includes('Birthday')) return '🎂';
    if (source.includes('Medical') || source.includes('Allergy')) return '💊';
    if (source.includes('Staff') || source.includes('Teacher') || source.includes('Director') || source.includes('Administrator')) return '👩‍🏫';
    if (source.includes('Transportation')) return '🚌';
    if (source.includes('Spreadsheet')) return '📊';
    return '🗂';
  };

  const getComposeAuthHeaders = () => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('kuddleToken');

    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const loadAudienceSource = async (source) => {
    setSelectedAudienceSource(source);
    setAudienceSearch('');
    setIsLoadingAudience(true);

    try {


      const audienceEndpoints = {
        'Parent Contacts': 'parent-contacts',
        'Classroom Rosters': 'classroom-rosters',
        'Authorized Pickup Contacts': 'authorized-pickups',
        'Emergency Contacts': 'emergency-contacts',
        'Attendance Groups': 'attendance-groups',
        'Missing Documents': 'missing-documents',
        'Birthday Lists': 'birthday-lists',
        'Medical Alerts': 'medical-alerts',
        'Allergy Groups': 'allergy-groups',
        'Staff Groups': 'staff-groups',
        Teachers: 'teachers',
        Directors: 'directors',
        Administrators: 'administrators',
        'Floaters/Substitutes': 'floaters',
        'Billing Lists': 'billing-lists',
        'Past Due Accounts': 'past-due',
        'Payment Plans': 'payment-plans',
        'Incident Reports': 'incident-reports',
        'Field Trip Groups': 'field-trips',
        'Transportation Lists': 'transportation',
        'Custom Saved Audiences': 'saved-audiences',
        'Spreadsheet Import': 'spreadsheet-import',
        Substitutes: 'substitutes',
        Kitchen: 'kitchen',
        'Transportation Staff': 'transportation-staff',
        Health: 'health',
        Office: 'office',
        Maintenance: 'maintenance',
      };

      const endpoint = `/api/audience/${audienceEndpoints[source]}`;

      if (!audienceEndpoints[source]) {
        setAudienceRecords([]);
        showBanner(`${source} will be connected next.`, 'info');
        return;
      }

      const res = await fetch(endpoint, {
        headers: getComposeAuthHeaders(),
      });

      if (!res.ok) {
        throw new Error('Failed to load audience source');
      }

      const data = await res.json();
      setAudienceRecords(data || []);
    } catch (error) {
      console.error('Audience load failed:', error);
      showBanner('Could not load audience records.', 'error');
    } finally {
      setIsLoadingAudience(false);
    }
  };

  // ==================================================
  // EFFECTS
  // Purpose: Loads saved drafts, saves drafts, applies initial values, and loads tags.
  // ==================================================

  useEffect(() => {
    const draft = localStorage.getItem('draftMessage');
    if (draft) {
      const parsed = JSON.parse(draft);
      setFormData(parsed.formData || {});
      setFolder(parsed.folder || 'Inbox');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('draftMessage', JSON.stringify({ formData, folder }));
  }, [formData, folder]);

  useEffect(() => {
    if (initialTo || initialSubject || initialBody) {
      setFormData((prev) => ({
        ...prev,
        to: initialTo,
        subject: initialSubject,
        body: initialBody,
      }));
    }
  }, [initialTo, initialSubject, initialBody]);

  useEffect(() => {
    setSuggestedTags(loadGlobalTags());
  }, []);

  useEffect(() => {
    const editor = bodyTextareaRef.current;
    if (!editor) return;

    if (document.activeElement === editor) return;

    const nextHtml = formData.body || '';

    if (editor.innerHTML !== nextHtml) {
      editor.innerHTML = nextHtml;
      lastEditorHtmlRef.current = nextHtml;
    }
  }, [formData.body]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        scheduleMenuRef.current &&
        !scheduleMenuRef.current.contains(event.target)
      ) {
        setShowScheduleMenu(false);
      }

      if (
        formatMenuRef.current &&
        !formatMenuRef.current.contains(event.target)
      ) {
        setShowFormatMenu(false);
      }

      if (
        textColorMenuRef.current &&
        !textColorMenuRef.current.contains(event.target)
      ) {
        setShowTextColorMenu(false);
      }

      if (
        alignMenuRef.current &&
        !alignMenuRef.current.contains(event.target)
      ) {
        setShowAlignMenu(false);
      }

      if (
        moreFormattingMenuRef.current &&
        !moreFormattingMenuRef.current.contains(event.target)
      ) {
        setShowMoreFormattingMenu(false);
      }
      if (
        moreToolsRef.current &&
        !moreToolsRef.current.contains(event.target)
      ) {
        setShowMoreToolsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ==================================================
  // HELPERS
  // Purpose: Folder lookup and reusable utility values.
  // ==================================================
  const getAvailableFolders = () => {
    const stored = JSON.parse(localStorage.getItem('messageFolders'));
    return stored && Array.isArray(stored) ? stored : folders;
  };

  // ==================================================
  // FORM HANDLERS
  // Purpose: Handles typing, sending, clearing, copy/download/upload, tags, emoji, and attachments.
  // ==================================================

  const applyTextFormat = (formatType, value = '') => {
    const textarea = bodyTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.body.slice(start, end);

    if (!selectedText) {
      showBanner('Select text first, then choose a formatting option.', 'info');
      return;
    }

    const safeValue = value ? String(value).toLowerCase().replace(/\s+/g, '-') : '';

    const formatMap = {
      bold: ['**', '**'],
      italic: ['_', '_'],
      underline: ['<u>', '</u>'],
      font: [`<span style="font-family:${value}">`, '</span>'],
      size: [`<span class="compose-size-${safeValue}">`, '</span>'],
    };

    const [openTag, closeTag] = formatMap[formatType] || ['', ''];
    const formattedText = `${openTag}${selectedText}${closeTag}`;

    setFormData((prev) => ({
      ...prev,
      body:
        prev.body.slice(0, start) +
        formattedText +
        prev.body.slice(end),
    }));

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + formattedText.length);
    }, 0);
  };

  const handleScheduleConfirm = () => {
    if (!scheduledDate || !scheduledTime) {
      showBanner('Choose a date and time before scheduling.', 'error');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      scheduleDate: scheduledDate,
      scheduleTime: scheduledTime,
    }));

    setShowScheduleModal(false);
    setShowScheduleMenu(false);
    showBanner(`Message scheduled for ${scheduledDate} at ${scheduledTime}.`, 'success');
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleEmojiClick = (emojiData) => {
    setFormData((prev) => ({
      ...prev,
      body: prev.body + emojiData.emoji,
    }));
    setShowEmojiPicker(false);
  };

  const handleAttachmentUpload = (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    setFormData((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...files],
    }));

    toast.info(`${files.length} file(s) attached.`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const { to, subject, body } = formData;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!to.trim() || !subject.trim() || !body.trim()) {
      showBanner('⚠ Please fill out all fields.', 'error');
      return;
    }

    if (!emailRegex.test(to)) {
      showBanner('⚠ Invalid recipient email address.', 'error');
      return;
    }

    setIsSending(true);

    onSend({
      ...formData,
      folder,
      tags,
    });

    setTimeout(() => setIsSending(false), 2000);

    if (onClose) onClose();

    localStorage.removeItem('draftMessage');

    setFormData({
      to: '',
      cc: '',
      bcc: '',
      subject: '',
      body: '',
      attachments: [],
      scheduleDate: '',
      scheduleTime: '',
    });

    setTagInput('');
    setTags([]);
    setFolder('Inbox');

    showBanner('✅ Message sent successfully!', 'success');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(formData));
    toast.info('📋 Message copied to clipboard.');
  };

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify({ formData, folder, tags }, null, 2)], {
      type: 'application/json',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'draftMessage.json';
    link.click();
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const uploaded = JSON.parse(event.target.result);

        setFormData(uploaded.formData || {});
        setFolder(uploaded.folder || 'Inbox');
        setTags(uploaded.tags || []);
      } catch {
        toast.error('⚠ Invalid file format.');
      }
    };

    reader.readAsText(file);
  };

  const handleClear = () => {
    setFormData({
      to: '',
      cc: '',
      bcc: '',
      subject: '',
      body: '',
      attachments: [],
      scheduleDate: '',
      scheduleTime: '',
    });

    setTagInput('');
    setTags([]);
    setFolder('Inbox');
    localStorage.removeItem('draftMessage');

    toast.info('🗑️ Form cleared.');
  };

  const handleSpam = () => {
    showBanner('🚫 Message flagged as spam.', 'info');
  };

  const scheduleTimes = [
    '05:30 AM',
    '06:00 AM',
    '06:30 AM',
    '07:00 AM',
    '07:30 AM',
    '08:00 AM',
    '08:30 AM',
    '09:00 AM',
    '09:30 AM',
    '10:00 AM',
    '10:30 AM',
    '11:00 AM',
    '11:30 AM',
    '12:00 PM',
    '12:30 PM',
    '01:00 PM',
    '01:30 PM',
    '02:00 PM',
    '02:30 PM',
    '03:00 PM',
    '03:30 PM',
    '04:00 PM',
    '04:30 PM',
    '05:00 PM',
    '05:30 PM',
    '06:00 PM',
  ];

  const colorRows = [
    ['#000000', '#444444', '#666666', '#999999', '#cccccc', '#eeeeee', '#ffffff'],
    ['#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#0000ff', '#9900ff', '#ff00ff'],
    ['#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#cfe2f3', '#d9d2e9', '#ead1dc'],
    ['#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8', '#a2c4c9', '#9fc5e8', '#b4a7d6', '#d5a6bd'],
    ['#e06666', '#f6b26b', '#ffd966', '#93c47d', '#76a5af', '#6fa8dc', '#8e7cc3', '#c27ba0'],
    ['#cc0000', '#e69138', '#f1c232', '#6aa84f', '#45818e', '#3d85c6', '#674ea7', '#a64d79'],
    ['#990000', '#b45f06', '#bf9000', '#38761d', '#134f5c', '#0b5394', '#351c75', '#741b47'],
  ];

  const renderColorPalette = (type) => (
    <div className="color-palette-grid">
      {colorRows.flat().map((color) => (
        <button
          key={`${type}-${color}`}
          type="button"
          className="color-swatch"
          style={{ backgroundColor: color }}
          title={type === 'text' ? `Set Text Color ${color}` : `Set Background Color ${color}`}
          aria-label={type === 'text' ? `Set Text Color ${color}` : `Set Background Color ${color}`}
          onClick={() => {
            applyTextFormat(type === 'text' ? 'textColor' : 'backgroundColor', color);
            setShowTextColorMenu(false);
          }}
        />
      ))}
    </div>
  );

  // ==================================================
  // RENDER
  // Purpose: Displays compose form, toolbar, tag editor, folder selector, and schedule modal.
  // ==================================================
  return (
    <div className={`card compose-card compose-${composeMode}`}>
      <div className="compose-window-header">
        <h3>{composeMode === 'minimized' ? 'Draft saved' : 'New Message'}</h3>

        <div className="compose-window-controls">
          <button
            type="button"
            onClick={() =>
              setComposeMode((prev) =>
                prev === 'minimized' ? 'page' : 'minimized'
              )
            }
            title={composeMode === 'minimized' ? 'Restore Compose Window' : 'Minimize Compose Window'}
            aria-label={composeMode === 'minimized' ? 'Restore Compose Window' : 'Minimize Compose Window'}

          >
            <Minus size={20} />
          </button>

          <button
            type="button"
            onClick={() =>
              setComposeMode((prev) => (prev === 'fullscreen' ? 'page' : 'fullscreen'))
            }
            title={composeMode === 'fullscreen' ? 'Exit Fullscreen' : 'Open Fullscreen'}
            aria-label={composeMode === 'fullscreen' ? 'Exit Fullscreen' : 'Open Fullscreen'}
          >
            {composeMode === 'fullscreen' ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>

          <button
            type="button"
            onClick={() => {
              localStorage.setItem('draftMessage', JSON.stringify({ formData, folder, tags }));
              if (onClose) onClose();
            }}
            title="Save Draft and Close"
            aria-label="Save Draft and Close"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {composeMode !== 'minimized' && (
        <form className="compose-form" onSubmit={handleSubmit}>
          <div className="address-row">
            <span className="address-label">To</span>

            <input
              type="email"
              name="to"
              className="address-input"
              title="Recipient Email Address"
              aria-label="Recipient Email Address"
              value={formData.to}
              onChange={handleChange}
            />
            <div className="address-actions">
              <button
                type="button"
                className="address-toggle-btn"
                title="Show Carbon Copy Field"
                aria-label="Show Carbon Copy Field"
                onClick={() => setShowCC((prev) => !prev)}
              >
                Cc
              </button>

              <button
                type="button"
                className="address-toggle-btn"
                title="Show Blind Carbon Copy Field"
                aria-label="Show Blind Carbon Copy Field"
                onClick={() => setShowBCC((prev) => !prev)}
              >
                Bcc
              </button>

              <div className="mail-merge-shell">
                <button
                  type="button"
                  className="mail-merge-icon-btn"
                  title="Open Personalized Send Menu"
                  aria-label="Open Personalized Send Menu"
                  aria-haspopup="menu"
                  aria-expanded={showMailMergeMenu}
                  onClick={() => setShowMailMergeMenu((prev) => !prev)}
                >
                  <UserRoundPlus size={24} />
                </button>

                {showMailMergeMenu && (
                  <div className="mail-merge-menu">
                    <button
                      type="button"
                      className="mail-merge-option"
                      title={mailMergeEnabled ? "Disable Personalized Send" : "Enable Personalized Send"}
                      aria-label={mailMergeEnabled ? "Disable Personalized Send" : "Enable Personalized Send"}
                      aria-pressed={mailMergeEnabled}
                      onClick={() => {
                        setMailMergeEnabled((prev) => !prev);
                        setShowMailMergeMenu(false);
                      }}
                    >
                      <UserRoundPlus size={20} />

                      <span>Personalized Send</span>

                      <input
                        type="checkbox"
                        checked={mailMergeEnabled}
                        readOnly
                        className="mail-merge-checkbox"
                      />
                    </button>

                    <button
                      type="button"
                      className="mail-merge-option"
                      title="Open Smart Audience Builder"
                      aria-label="Open Smart Audience Builder"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        setShowMailMergeMenu(false);
                        setSelectedAudienceSource(null);
                        setAudienceSearch('');
                        setShowSmartAudienceBuilder(true);
                      }}
                    >
                      <span>▦</span>
                      <span>Add from Roster Sheet</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {showCC && (
            <div className="address-row">
              <span className="address-label">Cc</span>

              <input
                type="email"
                name="cc"
                title="Carbon Copy Email Address"
                aria-label="Carbon Copy Email Address" className="address-input"
                value={formData.cc}
                onChange={handleChange}
              />
            </div>
          )}

          {showBCC && (
            <div className="address-row">
              <span className="address-label">Bcc</span>

              <input
                type="email"
                name="bcc"
                title="Blind Carbon Copy Email Address"
                aria-label="Blind Carbon Copy Email Address"
                className="address-input"
                value={formData.bcc}
                onChange={handleChange}
              />
            </div>
          )}

          <input
            type="text"
            name="subject"
            className="subject-input"
            placeholder="Subject"
            title="Message Subject"
            aria-label="Message Subject"
            value={formData.subject}
            onChange={handleChange}
            autoComplete="off"
            required
          />

          <div
            ref={bodyTextareaRef}
            className="message-textarea message-editor"
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            title="Message Body"
            aria-label="Message Body"
            data-placeholder="Write your message..."
            onInput={(e) => {
              const html = e.currentTarget?.innerHTML || '';
              lastEditorHtmlRef.current = html;

              setFormData((prev) => ({
                ...prev,
                body: html,
              }));
            }}
          />

          {mailMergeEnabled && (

            <div className="personalized-send-card">

              <div className="personalized-send-card-left">

                <div className="personalized-send-card-icon">
                  <MailCheck size={22} />
                </div>

                <div className="personalized-send-card-content">

                  <h4>Kuddlecore Personalized Send</h4>

                  <p>
                    Automatically personalize one message for every recipient using
                    Kuddlecore Smart Tags.
                  </p>

                </div>

              </div>

              <button
                type="button"
                className="personalized-send-help-btn"
                title="Open Kuddlecore Help Center"
                aria-label="Open Kuddlecore Help Center"
                onClick={() => setShowMailMergeHelp(true)}
              >
                <BookOpen size={16} />
                Kuddlecore Help Center
              </button>

            </div>

          )}

          {showEmojiPicker && (
            <div className="emoji-picker-wrapper" id="emoji-picker">
              <EmojiPicker onEmojiClick={handleEmojiClick} searchDisabled skinTonesDisabled height={300} />
            </div>
          )}

          <div className="compose-bottom-toolbar">
            <div className="send-button-group" ref={scheduleMenuRef}>
              <button
                type="submit"
                disabled={isSending}
                className="send-btn-main"
                title={isSending ? "Sending Message..." : "Send Message"}
                aria-label={isSending ? "Sending Message..." : "Send Message"}
              >
                {isSending ? 'Sending...' : 'Send'}
              </button>

              <button
                type="button"
                className="send-btn-dropdown"
                title="Open Schedule Send Options"
                aria-label="Open Schedule Send Options"
                aria-haspopup="menu"
                aria-expanded={showScheduleMenu}
                onClick={() => setShowScheduleMenu((prev) => !prev)}
              >
                <ChevronDown size={18} />
              </button>
              {showScheduleMenu && (
                <div className="schedule-send-menu">
                  <button
                    type="button"
                    title="Schedule Message for Tomorrow at 9:00 AM"
                    aria-label="Schedule Message for Tomorrow at 9:00 AM"
                    onClick={() => {
                      setScheduledTime('09:00 AM');
                      setShowScheduleMenu(false);
                    }}
                  >
                    Tomorrow at 9:00 AM
                  </button>

                  <button
                    type="button"
                    title="Schedule Message for Monday at 9:00 AM"
                    aria-label="Schedule Message for Monday at 9:00 AM"
                    onClick={() => {
                      setScheduledTime('09:00 AM');
                      setShowScheduleMenu(false);
                    }}
                  >
                    Monday at 9:00 AM
                  </button>

                  <button
                    type="button"
                    title="Choose a Custom Schedule Time"
                    aria-label="Choose a Custom Schedule Time"
                    onClick={() => {
                      setShowScheduleMenu(false);
                      setShowScheduleModal(true);
                    }}
                  >
                    Choose another time...
                  </button>
                </div>
              )}
            </div>

            <div className="format-menu-shell" ref={formatMenuRef}>
              <button
                type="button"
                className="compose-tool-btn format-toggle-btn"
                title="Open Formatting Options"
                aria-label="Open Formatting Options"
                aria-haspopup="menu"
                aria-expanded={showFormatMenu}
                onClick={() => setShowFormatMenu((prev) => !prev)}
              >
                Aa
              </button>

              {showFormatMenu && (
                <div className="format-menu-panel google-style-format-panel">
                  <select
                    className="format-select"
                    title="Select Font"
                    aria-label="Select Font"
                    value={selectedFont}
                    onChange={(e) => {
                      setSelectedFont(e.target.value);
                      applyTextFormat('font', e.target.value);
                    }}
                  >
                    {[
                      'Sans Serif',
                      'Serif',
                      'Fixed Width',
                      'Wide',
                      'Narrow',
                      'Comic Sans MS',
                      'Garamond',
                      'Georgia',
                      'Tahoma',
                      'Trebuchet MS',
                      'Verdana',
                      'Courier New',
                      'Lucida Console',
                    ].map((font) => (
                      <option key={font} value={font}>
                        {font}
                      </option>
                    ))}
                  </select>

                  <select
                    className="format-size-select"
                    title="Select Font Size"
                    aria-label="Select Font Size"
                    value={selectedSize}
                    onChange={(e) => {
                      setSelectedSize(e.target.value);
                      applyTextFormat('size', e.target.value);
                    }}
                  >
                    {['Tiny', 'Small', 'Medium', 'Large', 'X-Large', 'Huge'].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>

                  <button type="button" className="format-icon-btn bold-icon" title="Bold" aria-label="Bold" onClick={() => applyTextFormat('bold')}>
                    B
                  </button>

                  <button type="button" className="format-icon-btn italic-icon" title="Italic" aria-label="Italic" onClick={() => applyTextFormat('italic')}>
                    I
                  </button>

                  <button type="button" className="format-icon-btn underline-icon" title="Underline" aria-label="Underline" onClick={() => applyTextFormat('underline')}>
                    U
                  </button>

                  <div className="format-dropdown-shell" ref={textColorMenuRef}>
                    <button
                      type="button"
                      className="format-icon-btn text-color-icon"
                      title="Open Text Color Options"
                      aria-label="Open Text Color Options"
                      aria-haspopup="dialog"
                      aria-expanded={showTextColorMenu}
                      onClick={() => setShowTextColorMenu((prev) => !prev)}
                    >
                      <span>A</span>
                      <ChevronDown size={12} />
                    </button>

                    {showTextColorMenu && (
                      <div className="color-palette-popover">
                        <div>
                          <h4>Text Color</h4>
                          {renderColorPalette('text')}
                        </div>

                        <div>
                          <h4>Background Color</h4>
                          {renderColorPalette('background')}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="format-dropdown-shell" ref={alignMenuRef}>
                    <button
                      type="button"
                      className="format-icon-btn"
                      title="Open Alignment Options"
                      aria-label="Open Alignment Options"
                      aria-haspopup="menu"
                      aria-expanded={showAlignMenu}
                      onClick={() => setShowAlignMenu((prev) => !prev)}
                    >
                      <AlignLeft size={17} />
                      <ChevronDown size={12} />
                    </button>

                    {showAlignMenu && (
                      <div className="format-mini-menu">
                        <button type="button" title="Align Left" aria-label="Align Left" onClick={() => { applyTextFormat('alignLeft'); setShowAlignMenu(false); }}>
                          <AlignLeft size={17} /> Align Left
                        </button>

                        <button type="button" title="Align Center" aria-label="Align Center" onClick={() => { applyTextFormat('alignCenter'); setShowAlignMenu(false); }}>
                          <AlignCenter size={17} /> Align Center
                        </button>

                        <button type="button" title="Align Right" aria-label="Align Right" onClick={() => { applyTextFormat('alignRight'); setShowAlignMenu(false); }}>
                          <AlignRight size={17} /> Align Right
                        </button>
                      </div>
                    )}
                  </div>

                  <button type="button" className="format-icon-btn" title="Numbered List" aria-label="Numbered List" onClick={() => applyTextFormat('numberedList')}>
                    <ListOrdered size={19} />
                  </button>

                  <button type="button" className="format-icon-btn" title="Bulleted List" aria-label="Bulleted List" onClick={() => applyTextFormat('bulletList')}>
                    <List size={19} />
                  </button>

                  <div className="format-dropdown-shell" ref={moreFormattingMenuRef}>
                    <button
                      type="button"
                      className="format-icon-btn"
                      title="Open More Formatting Options"
                      aria-label="Open More Formatting Options"
                      aria-haspopup="menu"
                      aria-expanded={showMoreFormattingMenu}
                      onClick={() => setShowMoreFormattingMenu((prev) => !prev)}
                    >
                      <IndentDecrease size={18} />
                      <ChevronDown size={12} />
                    </button>

                    {showMoreFormattingMenu && (
                      <div className="format-mini-menu">
                        <button type="button" title="Remove Formatting" aria-label="Remove Formatting" onClick={() => { applyTextFormat('removeFormat'); setShowMoreFormattingMenu(false); }}>
                          <Eraser size={17} /> Remove Formatting
                        </button>

                        <button type="button" title="Strikethrough" aria-label="Strikethrough" onClick={() => { applyTextFormat('strike'); setShowMoreFormattingMenu(false); }}>
                          <Strikethrough size={17} /> Strikethrough
                        </button>

                        <button type="button" title="Block Quote" aria-label="Block Quote" onClick={() => { applyTextFormat('quote'); setShowMoreFormattingMenu(false); }}>
                          <Quote size={17} /> Quote
                        </button>

                        <button type="button" title="Decrease Indent" aria-label="Decrease Indent" onClick={() => { applyTextFormat('indentLess'); setShowMoreFormattingMenu(false); }}>
                          <IndentDecrease size={17} /> Indent Less
                        </button>

                        <button type="button" title="Increase Indent" aria-label="Increase Indent" onClick={() => { applyTextFormat('indentMore'); setShowMoreFormattingMenu(false); }}>
                          <IndentIncrease size={17} /> Indent More
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              className="compose-tool-btn"
              title="Attach Files"
              aria-label="Attach Files"
              onClick={() => attachmentInputRef.current.click()}
            >
              <Paperclip size={22} />
            </button>

            <button
              type="button"
              className="compose-tool-btn"
              title={showEmojiPicker ? "Close Emoji Picker" : "Open Emoji Picker"}
              aria-label={showEmojiPicker ? "Close Emoji Picker" : "Open Emoji Picker"}
              aria-haspopup="dialog"
              aria-expanded={showEmojiPicker}
              onClick={() => setShowEmojiPicker((prev) => !prev)}
            >
              <SmilePlus size={22} />
            </button>

            <button
              type="button"
              className="compose-tool-btn"
              title="Open More Options"
              aria-label="Open More Options"
              aria-haspopup="menu"
              aria-expanded={showMoreToolsMenu}
              onClick={() => setShowMoreToolsMenu((prev) => !prev)}
            >
              <MoreHorizontal size={24} />
            </button>

            <button
              type="button"
              className="compose-trash-btn"
              title="Discard Draft"
              aria-label="Discard Draft"
              onClick={handleClear}
            >
              <Trash2 size={22} />
            </button>

            <input
              type="file"
              multiple
              ref={attachmentInputRef}
              style={{ display: 'none' }}
              onChange={handleAttachmentUpload}
            />
          </div>

          {showScheduleModal && (
            <div
              className="schedule-modal-overlay"
              onClick={() => setShowScheduleModal(false)}
            >
              <div
                className="schedule-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="schedule-modal-close"
                  title="Close Schedule Window"
                  aria-label="Close Schedule Window"
                  onClick={() => setShowScheduleModal(false)}
                >
                  ×
                </button>

                <h2>Schedule message</h2>

                <div className="schedule-modal-row">
                  <input
                    type="date"
                    title="Select Schedule Date"
                    aria-label="Select Schedule Date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                  />

                  <select
                    title="Select Schedule Time"
                    aria-label="Select Schedule Time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                  >
                    {scheduleTimes.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="schedule-modal-actions">
                  <button
                    type="button"
                    title="Cancel Schedule"
                    aria-label="Cancel Schedule"
                    onClick={() => setShowScheduleModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="schedule-confirm-btn"
                    title="Schedule Message"
                    aria-label="Schedule Message"
                    onClick={handleScheduleConfirm}
                  >
                    Schedule
                  </button>
                </div>
              </div>
            </div>
          )}
          <p className="compose-helper-text">
            This sends an internal Kuddlecore message. Email delivery will be connected separately.
          </p>
          {showMailMergeHelp && (
            <div
              className="personalized-send-help-overlay"
              onClick={() => setShowMailMergeHelp(false)}
            >
              <div
                className="personalized-send-help-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="personalized-send-help-close"
                  title="Close Kuddlecore Help Center"
                  aria-label="Close Kuddlecore Help Center"
                  onClick={() => setShowMailMergeHelp(false)}
                >
                  ×
                </button>

                {/* ==================================================
          KUDDLECORE HELP CENTER
          Purpose:
          Explains Kuddlecore Personalized Send, Smart Tags,
          recipient sources, and future capabilities.
      ================================================== */}

                <div className="mail-merge-help-header">
                  <p className="modal-kicker">Kuddlecore Help Center</p>

                  <h2>Kuddlecore Personalized Send</h2>

                  <p>
                    Create one message that can personalize itself for families, staff,
                    classrooms, and recipient groups using Kuddlecore Smart Tags.
                  </p>
                </div>

                <div className="personalized-send-help-grid">
                  <section className="mail-merge-help-section">
                    <h3>📖 Overview</h3>
                    <p>
                      Personalized Send helps you send one message while making each recipient
                      feel like the message was written directly for them.
                    </p>
                  </section>

                  <section className="personalized-send-help-section">
                    <h3>🏷️ Smart Tags</h3>
                    <div className="smart-tag-grid">
                      <span>@firstName</span>
                      <span>@lastName</span>
                      <span>@childName</span>
                      <span>@classroom</span>
                      <span>@teacher</span>
                      <span>@centerName</span>
                      <span>@pickupTime</span>
                      <span>@balanceDue</span>
                      <span>@eventDate</span>
                      <span>@emergencyContact</span>
                    </div>
                  </section>

                  <section className="personalized-send-help-section">
                    <h3>👥 Recipient Groups</h3>
                    <ul>
                      <li>Parent Contacts</li>
                      <li>Classroom Rosters</li>
                      <li>Staff Groups</li>
                      <li>Emergency Pickup Lists</li>
                      <li>Custom Recipient Lists</li>
                    </ul>
                  </section>

                  <section className="personalized-send-help-section">
                    <h3>📊 Connected Data Sources</h3>
                    <ul>
                      <li>Attendance Groups</li>
                      <li>Billing Lists</li>
                      <li>Student Profiles</li>
                      <li>Authorized Pickups</li>
                      <li>Center Information</li>
                    </ul>
                  </section>

                  <section className="personalized-send-help-section">
                    <h3>💡 Best Practices</h3>
                    <ul>
                      <li>Preview personalized messages before sending.</li>
                      <li>Use clear Smart Tags that match Kuddlecore records.</li>
                      <li>Use recipient groups to avoid sending to the wrong audience.</li>
                    </ul>
                  </section>

                  <section className="personalized-send-help-section">
                    <h3>🚀 Coming Next</h3>
                    <ul>
                      <li>Live personalized preview</li>
                      <li>Roster and spreadsheet import</li>
                      <li>Saved recipient groups</li>
                      <li>Scheduled personalized messages</li>
                      <li>Smart audience filters</li>
                    </ul>
                  </section>
                </div>
              </div>
            </div>
          )}

          {showSmartAudienceBuilder &&
            createPortal(
              <div
                className="smart-audience-overlay"
                onClick={() => setShowSmartAudienceBuilder(false)}
              >
                <div
                  className="smart-audience-builder"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="personalized-send-help-close"
                    title="Close Smart Audience Builder"
                    aria-label="Close Smart Audience Builder"
                    onClick={() => setShowSmartAudienceBuilder(false)}
                  >
                    ×
                  </button>

                  <div className="mail-merge-help-header">
                    <p className="modal-kicker">Kuddlecore Smart Audience Builder</p>

                    <h2>Add Recipients</h2>

                    <p>
                      Build your audience using classrooms, family contacts, attendance,
                      billing, staff, safety records, or custom uploads.
                    </p>
                  </div>

                  <input
                    type="text"
                    placeholder="Search parents, staff, classrooms, billing groups, or smart filters..."
                    className="smart-audience-search"
                    title="Search Smart Audience"
                    aria-label="Search Smart Audience"
                  />

                  <div className="smart-audience-layout">
                    <div className="smart-audience-left">
                      {smartAudienceSources.map((section) => (
                        <section key={section.group} className="smart-audience-section">
                          <h3>{section.group}</h3>

                          <div className="smart-audience-card-list">
                            {section.sources.map((source) => (
                              <button
                                key={source}
                                type="button"
                                className={`smart-audience-card ${selectedAudienceSource === source ? 'selected' : ''
                                  }`}
                                title={`Select ${source}`}
                                aria-label={`Select ${source}`}
                                aria-pressed={selectedAudienceSource === source}
                                onClick={() => loadAudienceSource(source)}
                              >
                                <div className="smart-audience-card-top">
                                  <span className="smart-audience-card-icon">
                                    {getAudienceIcon(source)}
                                  </span>

                                  <strong>{source}</strong>
                                </div>

                                <span>{getAudienceCount(source)}</span>
                              </button>
                            ))}
                          </div>
                        </section>
                      ))}
                    </div>

                    <div className="smart-audience-right">
                      {!selectedAudienceSource && (
                        <div className="audience-placeholder">
                          <h3>Select a Recipient Source</h3>
                          <p>Choose a category on the left to build your audience.</p>
                        </div>
                      )}

                      {selectedAudienceSource && (
                        <div className="audience-detail-panel">
                          <h3>{selectedAudienceSource}</h3>

                          <input
                            className="smart-audience-search"
                            title={`Search ${selectedAudienceSource}`}
                            aria-label={`Search ${selectedAudienceSource}`}
                            placeholder={`Search ${selectedAudienceSource}`}
                            value={audienceSearch}
                            onChange={(e) => setAudienceSearch(e.target.value)}
                          />

                          <div className="audience-results">
                            <div className="audience-filter-row">
                              <select
                                title="Filter by Center"
                                aria-label="Filter by Center"
                              >
                                <option>All Centers</option>
                                <option>Main Center</option>
                                <option>Second Location</option>
                              </select>

                              <select
                                title="Filter by Classroom"
                                aria-label="Filter by Classroom"
                              >
                                <option>All Classrooms</option>
                                <option>Infants</option>
                                <option>Toddlers</option>
                                <option>Preschool A</option>
                              </select>

                              <select
                                title="Filter by Status"
                                aria-label="Filter by Status"
                              >
                                <option>All Statuses</option>
                                <option>Active</option>
                                <option>Absent Today</option>
                                <option>Past Due</option>
                              </select>
                            </div>

                            <div className="audience-preview-card">
                              <strong>Audience Preview</strong>
                              <span>{selectedRecipients.length} recipient(s) selected.</span>
                            </div>

                            {isLoadingAudience && (
                              <p className="audience-empty-note">Loading audience records...</p>
                            )}

                            <div className="audience-record-list">
                              {audienceRecords
                                .filter((record) =>
                                  record.name
                                    .toLowerCase()
                                    .includes(audienceSearch.toLowerCase())
                                )
                                .map((record) => (
                                  <label key={record.id} className="audience-record-row">
                                    <input
                                      type="checkbox"
                                      title={`Select ${record.name}`}
                                      aria-label={`Select ${record.name}`}
                                      checked={selectedRecipients.some(
                                        (item) => item.id === record.id
                                      )}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedRecipients((prev) => [...prev, record]);
                                        } else {
                                          setSelectedRecipients((prev) =>
                                            prev.filter((item) => item.id !== record.id)
                                          );
                                        }
                                      }}
                                    />

                                    <span>
                                      <strong>{record.name}</strong>
                                      <small>{record.type}</small>
                                    </span>
                                  </label>
                                ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="smart-audience-footer">
                    <div>
                      <strong>Selected Audience</strong>
                      <span>{selectedRecipients.length} recipient(s) selected</span>
                    </div>

                    <div className="smart-audience-footer-actions">
                      <button
                        type="button"
                        title="Cancel Smart Audience Selection"
                        aria-label="Cancel Smart Audience Selection"
                        onClick={() => setShowSmartAudienceBuilder(false)}
                      >
                        Cancel
                      </button>

                      <button
                        type="button"
                        className="smart-audience-add-btn"
                        title="Add Selected Recipients"
                        aria-label="Add Selected Recipients"
                        onClick={() => {
                          setShowSmartAudienceBuilder(false);
                          showBanner(`${selectedRecipients.length} recipient(s) added.`, 'success');
                        }}
                      >
                        Add Recipients
                      </button>
                    </div>
                  </div>
                </div>
              </div>,
              document.body
            )}
        </form>
      )}
    </div>
  );
};

export default ComposeMessage;
